import { FormattedMessage, useIntl } from 'react-intl'
import { useState } from 'react'
import { useAppModule } from '../appContext.ts'
import { usePromise } from '../../shared/lib/hooks.ts'
import { Loadable } from '../../shared/ui/loadable.tsx'
import { TextInput } from '../../shared/ui/textInput/textInput.tsx'
import { Button } from '../../shared/ui/button/button.tsx'
import Checkbox from '../../shared/ui/checkbox/checkbox.tsx'
import { cn } from '../../shared/lib/utils.ts'
import {
  NotificationApp,
  NotificationSettingsResponse,
  Student,
} from '../../shared/api/chatApi.ts'
import { ProfileTitle } from '../profile/ProfileComponents.tsx'
import { AppMessageKeys } from '../../shared/translations/messages.ts'

interface FormState {
  contact: string
  app: NotificationApp
  is_news: boolean
  is_reminders: boolean
  is_motivational: boolean
}

const defaultForm: FormState = {
  contact: '',
  app: 'telegram',
  is_news: false,
  is_reminders: false,
  is_motivational: false,
}

function appLabel(app: NotificationApp) {
  return app === 'telegram' ? 'Telegram' : 'Max'
}

export function NotificationsView(props: { withBack?: boolean }) {
  const appModule = useAppModule()
  const account = usePromise(() => appModule.chatsApi.getAccount())

  return (
    <Loadable data={account}>
      {(student) => (
        <NotificationsContent student={student} withBack={props.withBack} />
      )}
    </Loadable>
  )
}

function NotificationsContent(props: {
  student: Student
  withBack?: boolean
}) {
  const intl = useIntl()
  const appModule = useAppModule()
  const [consent, setConsent] = useState(
    Boolean(props.student.has_notification_consent),
  )
  const [saved, setSaved] = useState<NotificationSettingsResponse | null>(null)
  const [form, setForm] = useState<FormState>(defaultForm)
  const [saving, setSaving] = useState(false)
  const [contactError, setContactError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  if (consent) {
    const channel = saved
      ? `${appLabel(saved.app)} · ${saved.contact}`
      : null
    return (
      <div className="flex flex-col gap-16">
        <PageTitle titleId="notifications.title" withBack={props.withBack} />
        <p className="text-16 text-gray4">
          {channel ? (
            <FormattedMessage
              id="notifications.pushesConnected"
              values={{ channel }}
            />
          ) : (
            <FormattedMessage id="notifications.pushesConnectedGeneric" />
          )}
        </p>
      </div>
    )
  }

  const onSave = async () => {
    const contact = form.contact.trim()
    if (!contact) {
      setContactError(
        intl.formatMessage({ id: 'notifications.contactRequired' }),
      )
      return
    }
    setContactError(null)
    setFormError(null)
    setSaving(true)
    try {
      const response = await appModule.chatsApi.saveNotificationSettings({
        contact,
        app: form.app,
        is_news: form.is_news,
        is_reminders: form.is_reminders,
        is_motivational: form.is_motivational,
      })
      setSaved(response)
      setConsent(true)
    } catch (e) {
      setFormError(
        e instanceof Error
          ? e.message
          : intl.formatMessage({ id: 'error.somethingWentWrong' }),
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-20">
      <PageTitle titleId="notifications.title" withBack={props.withBack} />
      <p className="text-16 text-gray4">
        <FormattedMessage id="notifications.description" />
      </p>

      <label className="flex flex-col gap-8">
        <span className="text-14 font-bold">
          <FormattedMessage id="notifications.contactLabel" />
        </span>
        <TextInput
          bg="gray"
          value={form.contact}
          placeholder={intl.formatMessage({
            id: 'notifications.contactPlaceholder',
          })}
          onChange={(e) => {
            setForm((prev) => ({ ...prev, contact: e.target.value }))
            if (contactError) setContactError(null)
          }}
        />
        {contactError && (
          <span className="text-12 text-alert">{contactError}</span>
        )}
      </label>

      <div className="flex flex-col gap-8">
        <span className="text-14 font-bold">
          <FormattedMessage id="notifications.appLabel" />
        </span>
        <div className="flex gap-8">
          {(['telegram', 'max'] as const).map((app) => (
            <button
              key={app}
              type="button"
              className={cn(
                'h-44 flex-1 rounded-6 border-2 text-16 font-bold transition-colors',
                form.app === app
                  ? 'border-main bg-second-light text-main'
                  : 'border-purple bg-white text-black',
              )}
              onClick={() => {
                setForm((prev) => ({ ...prev, app }))
              }}
            >
              {appLabel(app)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-12">
        <span className="text-14 font-bold">
          <FormattedMessage id="notifications.typesLabel" />
        </span>
        <NotificationToggle
          checked={form.is_news}
          labelId="notifications.type.news"
          onToggle={() => {
            setForm((prev) => ({ ...prev, is_news: !prev.is_news }))
          }}
        />
        <NotificationToggle
          checked={form.is_reminders}
          labelId="notifications.type.reminders"
          onToggle={() => {
            setForm((prev) => ({ ...prev, is_reminders: !prev.is_reminders }))
          }}
        />
        <NotificationToggle
          checked={form.is_motivational}
          labelId="notifications.type.motivational"
          onToggle={() => {
            setForm((prev) => ({
              ...prev,
              is_motivational: !prev.is_motivational,
            }))
          }}
        />
      </div>

      {formError && <p className="text-14 text-alert">{formError}</p>}

      <Button
        bg="blue-gradient"
        size="extralarge"
        rounded="full"
        className="w-full"
        disabled={saving}
        onClick={() => {
          void onSave()
        }}
      >
        <FormattedMessage id="notifications.save" />
      </Button>
    </div>
  )
}

function PageTitle(props: { titleId: AppMessageKeys; withBack?: boolean }) {
  if (props.withBack) {
    return (
      <ProfileTitle title={<FormattedMessage id={props.titleId} />} />
    )
  }
  return (
    <h1 className="mb-8 text-22 font-bold">
      <FormattedMessage id={props.titleId} />
    </h1>
  )
}

function NotificationToggle(props: {
  checked: boolean
  labelId: AppMessageKeys
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      className="flex items-start gap-12 text-left"
      onClick={props.onToggle}
    >
      <Checkbox
        className="mt-2 shrink-0"
        checked={props.checked}
        handler={(e) => {
          e.stopPropagation()
          props.onToggle()
        }}
      />
      <span className="text-14 text-black">
        <FormattedMessage id={props.labelId} />
      </span>
    </button>
  )
}
